<template>
  <v-row class="app-preview">
    <v-col
      cols="12"
      md="9"
    >
      <v-card class="app-lead-editable">
        <!-- Header -->
        <div class="py-5 px-5">
          <Letterhead :width="'100%'" />

          <div class="d-flex flex-wrap justify-space-between mt-9">
            <!-- Date -->
            <div class="d-flex align-center">
              <span class="me-2 font-weight-medium text--primary">Date Issued: {{ dateToday }}</span>
            </div>
          </div>
        </div>

        <v-divider></v-divider>

        <!-- Customer Details -->
        <div class="py-5 px-5">
          <div class="mb-3 d-flex">
            <div class="px-3 flex-grow-1 font-weight-semibold">
              <v-row>
                <!-- Customer Name -->
                <v-col
                  cols="12"
                  sm="6"
                  :class="{ 'pl-0 mb-6 mb-sm-0': $vuetify.breakpoint.smAndUp, 'px-0': $vuetify.breakpoint.xsOnly }"
                >
                  <p class="font-weight-semibold payment-details-header">
                    Quoted To:
                  </p>
                  <v-text-field
                    v-model="lead.name"
                    single-line
                    dense
                    outlined
                    hide-details
                    placeholder="Customer Name (Optional)"
                    :disabled="!allowEdit || ((lead.status === 'Quoted' || lead.status === 'Informed') && (!allowQuoteEdit || userTeam === 'General Main Lead'))"
                  ></v-text-field>
                </v-col>

                <!-- Contact Number -->
                <v-col
                  v-if="userTeam !== 'Quotation Assistant'"
                  cols="12"
                  sm="6"
                  :class="{ 'pr-0 mb-6 mb-sm-0': $vuetify.breakpoint.smAndUp, 'px-0': $vuetify.breakpoint.xsOnly }"
                >
                  <p class="font-weight-semibold payment-details-header">
                    Contact No.:
                  </p>
                  <v-text-field
                    v-model="lead.contact_number"
                    single-line
                    dense
                    outlined
                    type="number"
                    :rules="[v => /[0-9\-().\s]{7}$/.test(v) || 'Must be valid contact number']"
                    hide-details="auto"
                    placeholder="Contact Number"
                    prefix="+673"
                    :disabled="!allowEdit || ((lead.status === 'Quoted' || lead.status === 'Informed') && (!allowQuoteEdit || userTeam === 'General Main Lead'))"
                    @mousewheel.native.prevent
                  ></v-text-field>
                </v-col>
              </v-row>
            </div>
          </div>
        </div>

        <v-divider></v-divider>

        <!-- Quotation Details -->
        <div class="py-5 px-5">
          <div class="mb-3 d-none d-md-flex">
            <div class="px-3 flex-grow-1 font-weight-semibold">
              <v-row>
                <v-col
                  cols="12"
                  sm="6"
                >
                  <span>Description</span>
                </v-col>
                <v-col
                  cols="12"
                  sm="3"
                >
                  <span>Amount</span>
                </v-col>
                <v-col
                  cols="12"
                  sm="3"
                >
                  <span>Summable Amount</span>
                </v-col>
              </v-row>
            </div>
            <div class="header-spacer"></div>
          </div>

          <!-- Insurance Product Type -->
          <v-card
            outlined
            class="d-flex pa-3"
          >
            <v-row>
              <v-col
                cols="12"
                sm="6"
              >
                <v-select
                  v-model="lead.insurance_product_id"
                  :items="insuranceProductOptions"
                  item-value="id"
                  item-text="name"
                  dense
                  outlined
                  hide-details="auto"
                  placeholder="Select Insurance Product Type"
                  @change="clearRoadTaxInfo()"
                  :disabled="(!allowEdit && !allowQuote) || ((lead.status === 'Quoted' || lead.status === 'Informed') && (!allowQuoteEdit || userTeam === 'General Main Lead'))"
                ></v-select>
              </v-col>
            </v-row>
          </v-card>

          <!-- Car Registration Number -->
          <v-card
            v-if="isMotorInsurance"
            outlined
            class="d-flex pa-3 mt-5"
          >
            <v-row>
              <v-col
                cols="12"
                sm="6"
              >
                <v-text-field
                  v-model="lead.car_registration_number"
                  single-line
                  dense
                  outlined
                  hide-details="auto"
                  placeholder="Car Registration Number"
                  :disabled="!allowEdit || ((lead.status === 'Quoted' || lead.status === 'Informed') && (!allowQuoteEdit || userTeam === 'General Main Lead'))"
                ></v-text-field>
              </v-col>
            </v-row>
          </v-card>

          <!-- Insurance -->
          <v-card
            outlined
            class="d-flex pa-3 mt-5"
          >
            <v-row>
              <v-col
                cols="12"
                sm="6"
              >
                <v-select
                  v-model="lead.insurance_id"
                  :items="insuranceOptions"
                  item-value="id"
                  item-text="name"
                  dense
                  outlined
                  hide-details="auto"
                  placeholder="Select Insurance"
                  :disabled="!allowEdit || ((lead.status === 'Quoted' || lead.status === 'Informed') && (!allowQuoteEdit || userTeam === 'General Main Lead'))"
                ></v-select>
              </v-col>
            </v-row>
          </v-card>

          <!-- Vehicle model -->
          <v-card
            v-if="lead.status !== 'New'"
            outlined
            class="d-flex pa-3 mt-5"
          >
            <v-row>
              <v-col
                cols="12"
                sm="6"
              >
                <v-text-field
                  v-model="lead.vehicle_model"
                  dense
                  outlined
                  hide-details="auto"
                  placeholder="Vehicle model"
                  :label="lead.vehicle_model == null ? '' : 'Vehicle model'"
                  :disabled="!allowQuote || ((lead.status === 'Quoted' || lead.status === 'Informed') && (!allowQuoteEdit || userTeam === 'General Main Lead'))"
                ></v-text-field>
              </v-col>
            </v-row>
          </v-card>

          <!-- Sum Insured -->
          <v-card
            v-if="lead.status !== 'New'"
            outlined
            class="d-flex pa-3 mt-5"
          >
            <v-row>
              <v-col
                cols="12"
                sm="6"
                class="d-flex align-center"
              >
                <p class="mb-0">
                  Sum Insured
                </p>
              </v-col>
              <v-col
                cols="12"
                sm="3"
              >
                <v-text-field
                  v-model="lead.sum_insured"
                  outlined
                  dense
                  min="0"
                  type="number"
                  hide-details="auto"
                  prefix="$"
                  :disabled="!allowQuote || ((lead.status === 'Quoted' || lead.status === 'Informed') && (!allowQuoteEdit || userTeam === 'General Main Lead'))"
                  @mousewheel.native.prevent
                ></v-text-field>
              </v-col>
            </v-row>
          </v-card>

          <!-- No Claim Discount -->
          <v-card
            v-if="lead.status !== 'New'"
            outlined
            class="d-flex pa-3 mt-5"
          >
            <v-row>
              <v-col
                cols="12"
                sm="6"
              >
                <v-text-field
                  v-model="lead.no_claim_discount"
                  dense
                  outlined
                  hide-details="auto"
                  placeholder="No Claim Discount %"
                  :label="lead.no_claim_discount == null ? '' : 'No Claim Discount %'"
                  :disabled="!allowQuote || ((lead.status === 'Quoted' || lead.status === 'Informed') && (!allowQuoteEdit || userTeam === 'General Main Lead'))"
                ></v-text-field>
              </v-col>
            </v-row>
          </v-card>

          <!-- Valid until -->
          <v-card
            v-if="lead.status !== 'New'"
            outlined
            class="d-flex pa-3 mt-5"
          >
            <v-row>
              <v-col
                cols="12"
                sm="6"
                class="d-flex align-center"
              >
                <p class="mb-0">
                  Valid until
                </p>
              </v-col>
              <v-col
                cols="12"
                sm="6"
              >
                <v-menu
                  v-model="isDateMenuOpen"
                  :close-on-content-click="false"
                  transition="scale-transition"
                  offset-y
                  min-width="auto"
                >
                  <template v-slot:activator="{ on, attrs }">
                    <v-text-field
                      :append-icon="icons.mdiCalendar"
                      :value="new Date(lead.expired_at).toLocaleDateString('en-GB')"
                      readonly
                      dense
                      outlined
                      hide-details
                      v-bind="attrs"
                      v-on="on"
                      :disabled="!allowQuote || ((lead.status === 'Quoted' || lead.status === 'Informed') && (!allowQuoteEdit || userTeam === 'General Main Lead'))"
                    ></v-text-field>
                  </template>
                  <v-date-picker
                    v-model="lead.expired_at"
                    color="primary"
                    :first-day-of-week="1"
                    @input="isDateMenuOpen = false; calculatedLeadPeriod()"
                  >
                  </v-date-picker>
                </v-menu>
              </v-col>
            </v-row>
          </v-card>

          <!-- Period -->
          <v-card
            v-if="lead.status !== 'New'"
            outlined
            class="d-flex pa-3 mt-5"
          >
            <v-row>
              <v-col
                cols="12"
                sm="6"
              >
                <v-text-field
                  v-model="lead.period"
                  dense
                  outlined
                  hide-details="auto"
                  placeholder="Period (days)"
                  :label="lead.period == null ? '' : 'Period (days)'"
                  disabled
                ></v-text-field>
              </v-col>
            </v-row>
          </v-card>

          <!-- Original Price -->
          <v-card
            v-if="lead.status !== 'New'"
            outlined
            class="d-flex pa-3 mt-5"
          >
            <v-row>
              <v-col
                cols="12"
                sm="6"
                class="d-flex align-center"
              >
                <p class="mb-0">
                  Original Price
                </p>
              </v-col>
              <v-col
                cols="12"
                sm="3"
              >
                <v-text-field
                  v-model="lead.original_price"
                  outlined
                  dense
                  min="0"
                  type="number"
                  hide-details="auto"
                  prefix="$"
                  :disabled="!allowQuote || ((lead.status === 'Quoted' || lead.status === 'Informed') && (!allowQuoteEdit || userTeam === 'General Main Lead'))"
                  @mousewheel.native.prevent
                ></v-text-field>
              </v-col>
            </v-row>
          </v-card>

          <!-- After Discount -->
          <v-card
            v-if="lead.status !== 'New'"
            outlined
            class="d-flex pa-3 mt-5"
          >
            <v-row>
              <v-col
                cols="12"
                sm="6"
                class="d-flex align-center"
              >
                <p class="mb-0">
                  After Discount
                </p>
              </v-col>
              <v-col
                cols="12"
                offset-sm="3"
                sm="3"
              >
                <v-text-field
                  v-model="lead.after_discount"
                  outlined
                  dense
                  min="0"
                  type="number"
                  hide-details="auto"
                  prefix="$"
                  :disabled="!allowQuote || ((lead.status === 'Quoted' || lead.status === 'Informed') && (!allowQuoteEdit || userTeam === 'General Main Lead'))"
                  @mousewheel.native.prevent
                ></v-text-field>
              </v-col>
            </v-row>
          </v-card>

          <!-- Customer self service -->
          <v-card
            v-if="isMotorInsurance"
            outlined
            class="d-flex pa-3 mt-5"
          >
            <v-row>
              <v-col
                cols="12"
                sm="6"
              >
                <v-select
                  v-model="lead.roadtax_self_service"
                  :items="[{ id: true, name: 'Yes' }, { id: false, name: 'No' }]"
                  item-value="id"
                  item-text="name"
                  dense
                  outlined
                  hide-details="auto"
                  placeholder="Customer self service?"
                  :label="lead.roadtax_self_service == null ? '' : 'Customer self service?'"
                  @change="updateRoadTaxInfo($event)"
                  :disabled="(!allowEdit && !allowQuote) || ((lead.status === 'Quoted' || lead.status === 'Informed') && (!allowQuoteEdit || userTeam === 'General Main Lead'))"
                ></v-select>
              </v-col>
            </v-row>
          </v-card>

          <!-- Road Tax Price -->
          <v-card
            v-if="isMotorInsurance && !lead.roadtax_self_service && lead.status !== 'New'"
            outlined
            class="d-flex pa-3 mt-5"
          >
            <v-row>
              <v-col
                cols="12"
                sm="6"
                class="d-flex align-center"
              >
                <p class="mb-0">
                  Road Tax Price
                </p>
              </v-col>
              <v-col
                cols="12"
                offset-sm="3"
                sm="3"
              >
                <v-text-field
                  v-model="lead.road_tax"
                  outlined
                  dense
                  min="0"
                  type="number"
                  hide-details="auto"
                  prefix="$"
                  :disabled="!allowQuote || ((lead.status === 'Quoted' || lead.status === 'Informed') && (!allowQuoteEdit || userTeam === 'General Main Lead'))"
                  @mousewheel.native.prevent
                ></v-text-field>
              </v-col>
            </v-row>
          </v-card>

          <!-- Require inspection -->
          <v-card
            v-if="isMotorInsurance && !lead.roadtax_self_service && lead.status !== 'New'"
            outlined
            class="d-flex pa-3 mt-5"
          >
            <v-row>
              <v-col
                cols="12"
                sm="6"
              >
                <v-select
                  v-model="lead.require_inspection"
                  :items="[{ id: true, name: 'Yes' }, { id: false, name: 'No' }]"
                  item-value="id"
                  item-text="name"
                  dense
                  outlined
                  hide-details="auto"
                  placeholder="Does vehicle need inspection?"
                  :label="lead.require_inspection == null ? '' : 'Does vehicle need inspection?'"
                  :disabled="!allowQuote || ((lead.status === 'Quoted' || lead.status === 'Informed') && (!allowQuoteEdit || userTeam === 'General Main Lead'))"
                ></v-select>
              </v-col>
            </v-row>
          </v-card>

          <!-- Pickup Location -->
          <v-card
            v-if="isMotorInsurance && !lead.roadtax_self_service"
            outlined
            class="d-flex pa-3 mt-5"
          >
            <v-row>
              <v-col
                cols="12"
                sm="6"
              >
                <v-select
                  v-model="lead.pickup_location_id"
                  :items="pickupLocationOptions"
                  item-value="id"
                  item-text="name"
                  dense
                  outlined
                  hide-details="auto"
                  placeholder="Select Road Tax Pickup Location"
                  :disabled="(!allowQuote && !allowEdit) || ((lead.status === 'Quoted' || lead.status === 'Informed') && !allowQuoteEdit)"
                ></v-select>
              </v-col>
            </v-row>
          </v-card>

          <!-- Additional Fees -->
          <v-card
            v-if="lead.status !== 'New'"
            outlined
            class="d-flex pa-3 mt-5"
          >
            <v-row>
              <v-col
                cols="12"
                sm="6"
                class="d-flex align-center"
              >
                <p class="mb-0">
                  Additional Fees
                </p>
              </v-col>
              <v-col
                cols="12"
                offset-sm="3"
                sm="3"
              >
                <v-text-field
                  v-model="lead.additional_fees"
                  outlined
                  dense
                  min="0"
                  type="number"
                  hide-details="auto"
                  prefix="$"
                  :disabled="!allowQuote || ((lead.status === 'Quoted' || lead.status === 'Informed') && (!allowQuoteEdit || userTeam === 'General Main Lead'))"
                  @mousewheel.native.prevent
                ></v-text-field>
              </v-col>
            </v-row>
          </v-card>

          <!-- Previous Policy -->
          <v-card
            outlined
            class="d-flex pa-3 mt-5"
          >
            <v-row>
              <v-col
                cols="12"
                sm="6"
              >
                <div v-if="lead.previous_policy_url">
                  <a
                    :href="lead.previous_policy_url"
                    target="_blank"
                  >
                    <small>{{ lead.previous_policy_name }}</small>
                    <v-icon
                      size="18"
                      color="primary"
                    >{{ icons.mdiOpenInNew }}</v-icon>
                  </a>
                </div>

                <v-file-input
                  v-model="newPolicy"
                  prepend-icon=""
                  :append-icon="icons.mdiPaperclip"
                  accept="application/pdf, image/png, image/jpg, image/jpeg"
                  show-size
                  dense
                  outlined
                  :rules="policyUploadRules"
                  :label="lead.previous_policy_url ? 'Upload new previous policy' : 'Upload previous policy'"
                  hide-details="auto"
                  class="mt-4"
                  :disabled="!allowEdit || !allowQuote"
                >
                  <template v-slot:selection="{ text }">
                    <v-chip
                      small
                      label
                      color="primary"
                    >
                      {{ text }}
                    </v-chip>
                  </template>

                  <template v-slot:append>
                    <v-progress-circular
                      v-if="uploadLoading"
                      color="primary"
                      indeterminate
                      size="25"
                    ></v-progress-circular>
                  </template>
                </v-file-input>
              </v-col>
            </v-row>
          </v-card>

          <!-- Lead Documents -->
          <v-card
            v-if="lead.status === 'Quoted' || lead.status === 'Informed'"
            outlined
            class="d-flex pa-3 mt-5"
          >
            <v-row>
              <v-col
                cols="12"
                sm="6"
              >
                <div v-if="lead.lead_documents_url">
                  <div
                    v-for="(sd, sd_index) in lead.lead_documents_url"
                    :key="`sd-${sd_index}`"
                  >
                    <a
                      :href="sd"
                      target="_blank"
                    >
                      <small>{{ lead.lead_documents_name[sd_index] }}</small>
                      <v-icon
                        size="18"
                        color="primary"
                      >{{ icons.mdiOpenInNew }}</v-icon>
                    </a>
                  </div>
                </div>

                <v-file-input
                  v-model="newLeadDocuments"
                  prepend-icon=""
                  :append-icon="icons.mdiPaperclip"
                  accept="application/pdf, image/png, image/jpg, image/jpeg"
                  show-size
                  dense
                  outlined
                  :rules="leadDocumentsUploadRules"
                  :label="lead.lead_documents_url ? 'Upload new documents' : 'Upload documents'"
                  hide-details="auto"
                  class="mt-4"
                  :multiple="true"
                  :disabled="!allowEdit || !allowQuote"
                  @change="directMultipleLeadDocuments($event)"
                >
                  <template v-slot:selection="{ text }">
                    <v-chip
                      small
                      label
                      color="primary"
                    >
                      {{ text }}
                    </v-chip>
                  </template>

                  <template v-slot:append>
                    <v-progress-circular
                      v-if="uploadLeadDocumentsLoading"
                      color="primary"
                      indeterminate
                      size="25"
                    ></v-progress-circular>
                  </template>
                </v-file-input>
              </v-col>
            </v-row>
          </v-card>

          <!-- Remarks -->
          <v-card
            outlined
            class="d-flex pa-3 mt-5"
          >
            <v-row>
              <v-col
                cols="12"
                sm="6"
              >
                <v-textarea
                  v-model="lead.remarks"
                  dense
                  outlined
                  rows="3"
                  hide-details="auto"
                  placeholder="Remarks"
                  :disabled="(!allowEdit && !allowQuote) || ((lead.status === 'Quoted' || lead.status === 'Informed') && !allowQuoteEdit)"
                ></v-textarea>
              </v-col>
            </v-row>
          </v-card>
        </div>

        <v-divider></v-divider>

        <!-- Lead Footer -->
        <div
          v-if="lead.status !== 'New'"
          class="py-5 px-5"
        >
          <!-- Issuer -->
          <v-row>
            <v-col
              v-if="lead.issuer"
              cols="12"
              sm="6"
              class="d-flex align-center"
            >
              <p class="mb-0 pr-2 font-weight-semibold">
                Issued By:
              </p>
              <p class="mb-0">
                {{ lead.issuer.nickname }}
              </p>
            </v-col>
            <v-col
              v-else
              cols="12"
              sm="6"
            ></v-col>

            <v-col
              cols="4"
              sm="3"
              class="d-flex align-center justify-end"
            >
              <p class="mb-0 font-weight-semibold">
                Total Cash/Bank transfer:
              </p>
            </v-col>

            <!-- Total -->
            <v-col
              cols="8"
              sm="3"
              :class="{ 'px-0': $vuetify.breakpoint.xsOnly }"
            >
              <p class="mb-0 text-xl font-weight-semibold">
                ${{ calculatedTotal }}
              </p>
            </v-col>
          </v-row>
          <v-row>
            <v-col
              cols="12"
              sm="6"
            ></v-col>

            <v-col
              cols="4"
              sm="3"
              class="d-flex align-center justify-end"
            >
              <p class="mb-0 font-weight-semibold">
                Total Visa:
              </p>
            </v-col>

            <!-- Total -->
            <v-col
              cols="8"
              sm="3"
              :class="{ 'px-0': $vuetify.breakpoint.xsOnly }"
            >
              <v-text-field
                v-model="lead.total_visa"
                outlined
                dense
                min="0"
                type="number"
                hide-details="auto"
                prefix="$"
                :disabled="!allowQuote || ((lead.status === 'Quoted' || lead.status === 'Informed') && (!allowQuoteEdit || userTeam === 'General Main Lead'))"
                @mousewheel.native.prevent
              ></v-text-field>
            </v-col>
          </v-row>
        </div>
      </v-card>
    </v-col>

    <v-col
      cols="12"
      md="3"
    >
      <v-card v-if="userTeam === 'Quotation Main Lead' && lead.status === 'New' && !allowEdit" class="mb-6">
        <v-card-text>
          <v-btn
            color="primary"
            block
            :loading="loading"
            :disabled="loading"
            @click="allowEdit = true"
          >
            Edit New Lead
          </v-btn>
        </v-card-text>
      </v-card>
      <v-card v-if="(userTeam === 'Quotation Main Lead' || userTeam === 'Quotation Assistant' || lead.created_by_id === userData.id) && !allowQuoteEdit && (lead.status === 'Quoted' || lead.status === 'Informed')" class="mb-6">
        <v-card-text>
          <v-btn
            color="primary"
            block
            :loading="loading"
            :disabled="loading"
            @click="allowEdit = true; allowQuote = true; allowQuoteEdit = true"
          >
            Edit Quoted Lead
          </v-btn>
        </v-card-text>
      </v-card>
      <v-card class="mb-6">
        <v-card-text v-if="allowEdit && lead.status === 'New'">
          <v-btn
            color="primary"
            block
            :loading="loading"
            :disabled="loading"
            @click="handleUploads"
          >
            {{ t('Confirm Lead') }}
          </v-btn>

          <div class="text-center mt-4">
            {{ t('Please ensure that all fields are filled correctly as this record will not be editable after confirming.') }}
          </div>
        </v-card-text>
        <v-card-text v-if="(userTeam === 'Quotation Main Lead' || userTeam === 'Quotation Assistant') && lead.status === 'New' && !allowEdit">
          <v-btn
            color="primary"
            block
            :loading="loading"
            :disabled="loading"
            @click="beginQuote"
          >
            Begin Quote
          </v-btn>

          <div class="text-center mt-4">
            {{ t('Please ensure that all fields are filled correctly as this record will not be editable after starting quote.') }}
          </div>
        </v-card-text>
        <v-card-text v-else-if="allowQuoteEdit && (lead.status === 'Quoted' || lead.status === 'Informed')">
          <v-btn
            color="primary"
            block
            :loading="loading"
            :disabled="loading"
            @click="handleUploads(true)"
          >
            Confirm Quote
          </v-btn>
        </v-card-text>
        <v-card-text v-else-if="(userTeam === 'Quotation Main Lead' || userTeam === 'Quotation Assistant') && lead.status === 'Pending' && allowQuote">
          <v-btn
            color="primary"
            block
            :loading="loading"
            :disabled="loading"
            @click="confirmQuote"
          >
            Confirm Quote
          </v-btn>

          <div v-if="userTeam === 'Quotation Assistant'" class="text-center mt-4">
            {{ t('Please ensure that quoted amount is filled correctly as this record will not be editable after confirming.') }}
          </div>
        </v-card-text>
      </v-card>
      <v-card class="mb-6">
        <v-card-text v-if="(userTeam === 'General Main Lead'  || userTeam === 'Quotation Main Lead') && ((lead.status === 'Quoted' || lead.status === 'Informed') && !allowQuoteEdit)">
          <v-btn
            color="primary"
            block
            :loading="downloadLoading"
            :disabled="downloadLoading"
            @click="downloadPdf()"
          >
            <v-icon size="18">
              {{ icons.mdiDownload }}
            </v-icon>
            Download PDF
          </v-btn>
        </v-card-text>
      </v-card>
      <v-card
        v-if="errors.length != 0"
        class="mb-6"
      >
        <v-card-text>
          <Alert
            :messages="errors"
            color="error"
          ></Alert>
        </v-card-text>
      </v-card>
    </v-col>
  </v-row>
</template>

<script>
import { mdiCalendar, mdiPaperclip, mdiOpenInNew } from '@mdi/js'
import {
  inject,
  onMounted,
  onUnmounted,
  computed,
  ref,
} from '@vue/composition-api'
import { useRouter } from '@core/utils'
import { Letterhead } from '@/components/apps/previews'
import Alert from '@/components/Alert'

// import notificationService from '@/services/notification.service'
import leadStoreModule from './leadStoreModule'
import receiptStoreModule from '../receipt/receiptStoreModule'

export default {
  components: {
    Alert,
    Letterhead,
  },
  setup() {
    // Initializers
    const store = inject('store')
    const snackbarService = inject('snackbarService')
    const t = inject('t')
    const { router } = useRouter()
    const LEAD_APP_STORE_MODULE_NAME = 'app-lead'
    const RECEIPT_APP_STORE_MODULE_NAME = 'app-receipt'

    if (!store.hasModule(LEAD_APP_STORE_MODULE_NAME)) {
      store.registerModule(LEAD_APP_STORE_MODULE_NAME, leadStoreModule)
    }

    if (!store.hasModule(RECEIPT_APP_STORE_MODULE_NAME)) {
      store.registerModule(RECEIPT_APP_STORE_MODULE_NAME, receiptStoreModule)
    }

    // Properties
    const lead = ref({
      name: null,
      contact_number: null,
      insurance_product_id: null,
      insurance_id: null,
      car_registration_number: null,
      roadtax_self_service: null,
      require_inspection: null,
      vehicle_model: null,
      pickup_location_id: null,
      previous_policy: null,
      lead_documents: null,
      remarks: null,
      no_claim_discount: null,
      period: null,
      sum_insured: null,
      after_discount: null,
      road_tax: null,
      additional_fees: null,
      status: null,
      issuer: null,
      total_visa: null,
      original_price: null,
      expired_at: new Date(new Date().getFullYear(), new Date().getMonth(), new Date().getDate() + 31).toISOString().substring(0, 10),
    })
    const errors = ref([])
    const userData = JSON.parse(localStorage.getItem('userData'))
    const userTeam = computed(() => store.state.user.team)
    const dateToday = new Date().toLocaleDateString('en-SG')
    const loading = ref(false)
    const uploadLoading = ref(false)
    const uploadLeadDocumentsLoading = ref(false)
    const downloadLoading = ref(false)
    const updateAfterUpload = ref(false)
    const newPolicy = ref(null)
    const newLeadDocuments = ref(null)
    const uploadedPolicy = ref(null)
    const uploadedLeadDocuments = ref([])
    const policyUploadRules = [value => !value || value.size < 5000000 || 'Policy file has to be lower than 5MB']
    const leadDocumentsUploadRules = [value => !value || value.map(v => v.size).reduce((acc, current) => acc + current, 0) < 5000000 || 'Document file has to be lower than 5MB']
    const allowEdit = ref(null)
    const allowQuote = ref(null)
    const allowQuoteEdit = ref(null)
    const insuranceProductOptions = ref([])
    const insuranceOptions = ref([])
    const pickupLocationOptions = ref([])
    const motorInsuranceId = ref(null)
    const isDateMenuOpen = ref(false)

    // const appCable = notificationService.webSocketConnection()

    // Methods
    const fetchInsuranceProducts = () => {
      store
        .dispatch('app-receipt/fetchInsuranceProducts')
        .then(response => {
          const { data } = response.data
          insuranceProductOptions.value = data
          motorInsuranceId.value = insuranceProductOptions.value.filter(product => product.name === 'Motor insurance')[0].id
        })
        .catch(error => {
          snackbarService.error(error.response.data.message || 'Something went wrong while fetching insurance products. Please refresh!')
        })
    }
    const fetchInsurances = () => {
      store
        .dispatch('app-receipt/fetchInsurances')
        .then(response => {
          const { data } = response.data
          insuranceOptions.value = data
        })
        .catch(error => {
          snackbarService.error(error.response.data.message || 'Something went wrong while fetching insurances. Please refresh!')
        })
    }
    const fetchPickupLocations = () => {
      store
        .dispatch('app-receipt/fetchPickupLocations')
        .then(response => {
          const { data } = response.data
          pickupLocationOptions.value = data
        })
        .catch(error => {
          snackbarService.error(error.response.data.message || 'Something went wrong while fetching pickup location. Please refresh!')
        })
    }
    const parseCurrency = value => (value === null ? null : value.replace(/[$,]/g, ''))
    const fetchLead = () => {
      store
        .dispatch('app-lead/fetchLead', { id: router.currentRoute.params.id })
        .then(response => {
          const existingLead = response.data.data

          allowQuoteEdit.value = router.currentRoute.params.quote_edit

          lead.value.id = existingLead.id
          lead.value.created_at = existingLead.created_at
          lead.value.name = existingLead.name
          lead.value.contact_number = existingLead.contact_number
          lead.value.car_registration_number = existingLead.car_registration_number
          lead.value.insurance_product_id = existingLead.insurance_product.id
          lead.value.insurance_id = existingLead.insurance.id
          lead.value.vehicle_model = existingLead.vehicle_model
          lead.value.roadtax_self_service = Boolean(existingLead.roadtax_self_service)
          lead.value.require_inspection = Boolean(existingLead.require_inspection)
          lead.value.no_claim_discount = existingLead.no_claim_discount
          lead.value.period = existingLead.period
          lead.value.pickup_location_id = existingLead.pickup_location ? existingLead.pickup_location.id : null
          lead.value.sum_insured = parseCurrency(existingLead.sum_insured)
          lead.value.after_discount = parseCurrency(existingLead.after_discount)
          lead.value.road_tax = parseCurrency(existingLead.road_tax)
          lead.value.additional_fees = parseCurrency(existingLead.additional_fees)
          lead.value.total_visa = existingLead.total_visa !== '$0.00' ? parseCurrency(existingLead.total_visa) : null
          lead.value.original_price = existingLead.original_price !== '$0.00' ? parseCurrency(existingLead.original_price) : null
          lead.value.remarks = existingLead.remarks
          lead.value.status = existingLead.status
          lead.value.previous_policy = existingLead.previous_policy
          lead.value.previous_policy_url = existingLead.previous_policy_url
          lead.value.previous_policy_name = existingLead.previous_policy_name
          lead.value.lead_documents = existingLead.lead_documents
          lead.value.lead_documents_url = existingLead.lead_documents_url
          lead.value.lead_documents_name = existingLead.lead_documents_name
          lead.value.issuer = existingLead.quoted_by ? existingLead.quoted_by : null
          lead.value.created_by_id = existingLead.created_by.id
          if (existingLead.expired_at) lead.value.expired_at = existingLead.expired_at.split('/').reverse().join('-')
          allowEdit.value = (existingLead.created_by.id === userData.id && userTeam.value === 'General Main Lead') || allowQuoteEdit.value
          allowQuote.value = (existingLead.quoted_by ? existingLead.quoted_by.id === userData.id && existingLead.status === 'Pending' : false)
            || ((lead.value.status === 'Quoted' || lead.value.status === 'Informed') && allowQuoteEdit.value)
        })
        .catch(error => {
          snackbarService.error(error.response.data.message || 'Something went wrong while fetching lead. Please refresh!')
        })
    }
    const updateLead = (policy = null, sd = null) => {
      delete lead.value.issuer
      delete lead.value.previous_policy_url
      delete lead.value.previous_policy_name
      delete lead.value.lead_documents_url
      delete lead.value.lead_documents_name
      const updateParam = {
        id: router.currentRoute.params.id,
        name: lead.value.name,
        contact_number: lead.value.contact_number,
        insurance_product_id: lead.value.insurance_product_id,
        insurance_id: lead.value.insurance_id,
        car_registration_number: lead.value.car_registration_number,
        roadtax_self_service: lead.value.roadtax_self_service,
        pickup_location_id: lead.value.pickup_location_id,
        remarks: lead.value.remarks,
        sum_insured: lead.value.sum_insured,
        after_discount: lead.value.after_discount,
        road_tax: lead.value.road_tax,
        additional_fees: lead.value.additional_fees,
        total_visa: lead.value.total_visa,
        original_price: lead.value.original_price,
        require_inspection: lead.value.require_inspection,
        no_claim_discount: lead.value.no_claim_discount,
        period: lead.value.period,
        vehicle_model: lead.value.vehicle_model,
        expired_at: lead.value.expired_at,
      }
      if (policy) {
        lead.value.previous_policy = policy.signed_id
        updateParam.previous_policy = lead.value.previous_policy
      }
      if (sd) {
        lead.value.lead_documents = sd.map(s => s.signed_id)
        updateParam.lead_documents = lead.value.lead_documents
      }
      store
        .dispatch('app-lead/updateLead', updateParam)
        .then(response => {
          loading.value = false
          snackbarService.success(response.data.message)
          switch (response.data.data.status) {
            case 'New':
            case 'Pending':
              router.push('/apps/leads/new/list')
              break
            case 'Quoted':
            case 'Informed':
              router.push('/apps/leads/quoted/list')
              break
            case 'Issue':
              router.push('/apps/leads/issue/list')
              break
            default:
          }
        })
        .catch(error => {
          loading.value = false
          errors.value = error.response.data.errors
          snackbarService.error(error.response.data.message || 'Something went wrong while updating lead. Please refresh!')
        })
    }
    const editQuote = (policy = null, sd = null) => {
      delete lead.value.issuer
      delete lead.value.previous_policy_url
      delete lead.value.previous_policy_name
      delete lead.value.lead_documents_url
      delete lead.value.lead_documents_name
      const updateParam = {
        id: router.currentRoute.params.id,
        name: lead.value.name,
        contact_number: lead.value.contact_number,
        insurance_product_id: lead.value.insurance_product_id,
        insurance_id: lead.value.insurance_id,
        car_registration_number: lead.value.car_registration_number,
        roadtax_self_service: lead.value.roadtax_self_service,
        pickup_location_id: lead.value.pickup_location_id,
        remarks: lead.value.remarks,
        sum_insured: lead.value.sum_insured,
        after_discount: lead.value.after_discount,
        road_tax: lead.value.road_tax,
        additional_fees: lead.value.additional_fees,
        total_visa: lead.value.total_visa,
        original_price: lead.value.original_price,
        require_inspection: lead.value.require_inspection,
        no_claim_discount: lead.value.no_claim_discount,
        period: lead.value.period,
        vehicle_model: lead.value.vehicle_model,
        expired_at: lead.value.expired_at,
      }
      if (policy) {
        lead.value.previous_policy = policy.signed_id
        updateParam.previous_policy = lead.value.previous_policy
      }
      if (sd) {
        lead.value.lead_documents = sd.map(s => s.signed_id)
        updateParam.lead_documents = lead.value.lead_documents
      }
      store
        .dispatch('app-lead/updateLead', updateParam)
        .then(response => {
          loading.value = false
          snackbarService.success(response.data.message)
          router.go(0)
        })
        .catch(error => {
          loading.value = false
          errors.value = error.response.data.errors
          snackbarService.error(error.response.data.message || 'Something went wrong while editing quote. Please refresh!')
        })
    }
    const directUploadPolicy = (file, edit = false) => {
      if (file == null) {
        lead.value.previous_policy = null
        lead.value.previous_policy_name = null

        return
      }

      uploadLoading.value = true
      store
        .dispatch('app-lead/directUploadPolicy', file)
        .then(response => {
          uploadLoading.value = false
          if (response) {
            uploadedPolicy.value = response
            if (!newLeadDocuments.value) {
              if (edit) {
                editQuote(uploadedPolicy.value)
              } else {
                updateLead(uploadedPolicy.value)
              }
            }
          }
        })
        .catch(error => {
          uploadLoading.value = false
          snackbarService.error(error.response.data.message || 'Something went wrong while uploading policy. Please refresh!')
        })
    }
    const directUploadLeadDocuments = file => {
      store
        .dispatch('app-lead/directUploadLeadDocuments', file)
        .then(response => {
          if (response) {
            uploadedLeadDocuments.value.push(response)
          }
        })
        .catch(error => {
          snackbarService.error(error.response.data.message || 'Something went wrong while uploading file. Please refresh!')
        })
    }
    const directMultipleLeadDocuments = array => {
      uploadLeadDocumentsLoading.value = true
      array.forEach(file => {
        directUploadLeadDocuments(file)
      })
      updateAfterUpload.value = true
      uploadLeadDocumentsLoading.value = false
    }
    const handleUploads = (edit = false) => {
      const uploadFile = newPolicy.value
      if (uploadFile) directUploadPolicy(uploadFile, true)

      if (updateAfterUpload) {
        if (updateAfterUpload.value) {
          if (edit) {
            editQuote(uploadedPolicy.value, uploadedLeadDocuments.value)
          } else {
            updateLead(uploadedPolicy.value, uploadedLeadDocuments.value)
          }
        }
      }
      if (!newPolicy.value && !newLeadDocuments.value) updateLead()
    }

    /*
    const fetchLeadCounts = () => {
      store
        .dispatch('fetchLeadCounts')
        .then(response => {
          const { data } = response.data
          store.dispatch('setLeadCounts', data)
        })
        .catch(error => {
          snackbarService.error(error.response.data.message || 'Something went wrong while fetching lead counts. Please refresh!')
        })
    }
    */

    const beginQuote = () => {
      store
        .dispatch('app-lead/beginQuote', { id: router.currentRoute.params.id })
        .then(response => {
          loading.value = false
          snackbarService.success(response.data.message)
          const existingLead = response.data.data

          lead.value.id = existingLead.id
          lead.value.created_at = existingLead.created_at
          lead.value.name = existingLead.name
          lead.value.contact_number = existingLead.contact_number
          lead.value.car_registration_number = existingLead.car_registration_number
          lead.value.insurance_product_id = existingLead.insurance_product.id
          lead.value.insurance_id = existingLead.insurance.id
          lead.value.vehicle_model = existingLead.vehicle_model
          lead.value.roadtax_self_service = Boolean(existingLead.roadtax_self_service)
          lead.value.require_inspection = Boolean(existingLead.require_inspection)
          lead.value.no_claim_discount = existingLead.no_claim_discount
          lead.value.period = existingLead.period || '30'
          lead.value.pickup_location_id = existingLead.pickup_location ? existingLead.pickup_location.id : null
          lead.value.sum_insured = parseCurrency(existingLead.sum_insured)
          lead.value.after_discount = parseCurrency(existingLead.after_discount)
          lead.value.road_tax = parseCurrency(existingLead.road_tax)
          lead.value.additional_fees = parseCurrency(existingLead.additional_fees)
          lead.value.remarks = existingLead.remarks
          lead.value.status = existingLead.status
          lead.value.previous_policy = existingLead.previous_policy
          lead.value.previous_policy_url = existingLead.previous_policy_url
          lead.value.previous_policy_name = existingLead.previous_policy_name
          lead.value.issuer = existingLead.quoted_by ? existingLead.quoted_by : null
          allowEdit.value = existingLead.created_by.id === userData.id
          allowQuote.value = existingLead.quoted_by ? existingLead.quoted_by.id === userData.id && existingLead.status === 'Pending' : false

          // fetchLeadCounts()
        })
        .catch(error => {
          loading.value = false
          errors.value = error.response.data.errors
          snackbarService.error(error.response.data.message || 'Something went wrong while starting quote. Please refresh!')
        })
    }
    const confirmQuote = () => {
      store
        .dispatch('app-lead/confirmQuote', {
          id: router.currentRoute.params.id,
          no_claim_discount: lead.value.no_claim_discount,
          period: lead.value.period,
          vehicle_model: lead.value.vehicle_model,
          sum_insured: lead.value.sum_insured,
          after_discount: lead.value.after_discount,
          road_tax: lead.value.road_tax,
          additional_fees: lead.value.additional_fees,
          total_visa: lead.value.total_visa,
          original_price: lead.value.original_price,
          expired_at: lead.value.expired_at,
          roadtax_self_service: lead.value.roadtax_self_service,
          insurance_product_id: lead.value.insurance_product_id,
          remarks: lead.value.remarks,
          require_inspection: lead.value.require_inspection,
        })
        .then(response => {
          loading.value = false
          snackbarService.success(response.data.message)
          router.push('/apps/leads/quoted/list')
        })
        .catch(error => {
          loading.value = false
          errors.value = error.response.data.errors
          snackbarService.error(error.response.data.message || 'Something went wrong while confirming quote. Please refresh!')
        })
    }
    const clearRoadTaxInfo = () => {
      lead.value.car_registration_number = null
      lead.value.require_inspection = null
      lead.value.road_tax = null
      lead.value.pickup_location_id = null
    }
    const updateRoadTaxInfo = event => {
      if (event) {
        lead.value.require_inspection = null
        lead.value.road_tax = null
        lead.value.pickup_location_id = null
      }
    }
    const calculatedLeadPeriod = () => {
      const dateNow = new Date()
      const dateValid = new Date(lead.value.expired_at)
      const diffTime = Math.abs(dateValid - dateNow)
      const diffDays = Math.floor(diffTime / (1000 * 60 * 60 * 24))

      lead.value.period = diffDays
    }
    const downloadPdf = () => {
      downloadLoading.value = true
      store
        .dispatch('app-lead/downloadPdf', { id: lead.value.id })
        .then(response => {
          const url = window.URL.createObjectURL(response)
          const a = document.createElement('a')
          a.href = url
          const carNumber = lead.value.car_registration_number && lead.value.car_registration_number !== 'NULL' ? `${lead.value.car_registration_number}_` : ''
          const contactNo = `${lead.value.contact_number}_`
          a.download = `${carNumber}${contactNo}${lead.value.created_at.split('/').join('-')}.pdf`
          document.body.appendChild(a)
          a.click()
          a.remove()
          downloadLoading.value = false
        })
        .catch(error => {
          downloadLoading.value = false
          snackbarService.error(error.response.data.message || 'Something went wrong while downloading pdf. Please refresh!')
        })
    }

    /*
    const updateLeads = () => {
      appCable.subscriptions.create({
        channel: 'LeadChannel',
      },
      {
        connected() {
          console.log('Connected to the leads channel')
        },
        received(data) {
          switch (data.event) {
            case 'new_lead':
              fetchLeadCounts()
              break
            case 'quoted_lead':
              fetchLeadCounts()
              break
            case 'refresh_leads':
              fetchLeadCounts()
              break
            case 'issue_lead':
              fetchLeadCounts()
              break
            default:
          }
        },
        disconnected() {
          console.log('Disconnected from the leads channel')
        },
      })
    }
    */

    // Computed
    const isMotorInsurance = computed(() => (motorInsuranceId.value === lead.value.insurance_product_id))
    const calculatedTotal = computed(() => {
      const afterDiscount = parseFloat(lead.value.after_discount) || 0.00
      const roadTax = parseFloat(lead.value.road_tax) || 0.00
      const additionalFees = parseFloat(lead.value.additional_fees) || 0.00

      return parseFloat(afterDiscount + roadTax + additionalFees).toFixed(2)
    })

    // Mounted
    onMounted(() => {
      fetchInsuranceProducts()
      fetchInsurances()
      fetchPickupLocations()
      fetchLead()
      if (!lead.value.period) {
        setTimeout(() => {
          calculatedLeadPeriod()
        }, 700)
      }

      // updateLeads()
    })

    // Unmounted
    onUnmounted(() => {
      if (store.hasModule(LEAD_APP_STORE_MODULE_NAME)) store.unregisterModule(LEAD_APP_STORE_MODULE_NAME)
      if (store.hasModule(RECEIPT_APP_STORE_MODULE_NAME)) store.unregisterModule(RECEIPT_APP_STORE_MODULE_NAME)
    })

    return {
      t,
      lead,
      errors,
      userData,
      userTeam,
      dateToday,
      loading,
      directUploadPolicy,
      directMultipleLeadDocuments,
      handleUploads,
      uploadLoading,
      uploadLeadDocumentsLoading,
      downloadPdf,
      downloadLoading,
      newPolicy,
      newLeadDocuments,
      updateAfterUpload,
      uploadedPolicy,
      policyUploadRules,
      leadDocumentsUploadRules,
      allowEdit,
      allowQuote,
      allowQuoteEdit,
      updateLead,
      beginQuote,
      confirmQuote,
      editQuote,
      insuranceProductOptions,
      pickupLocationOptions,
      motorInsuranceId,
      clearRoadTaxInfo,
      updateRoadTaxInfo,
      isMotorInsurance,
      calculatedTotal,
      insuranceOptions,
      isDateMenuOpen,
      calculatedLeadPeriod,

      icons: {
        mdiCalendar,
        mdiPaperclip,
        mdiOpenInNew,
      },
    }
  },
}
</script>

<style lang="scss">
@import '~@core/preset/preset/apps/style.scss';

.app-lead-editable {
  .lead-add-branch {
    width: 15rem;
  }
}
</style>
